// Tab White labels: os WLs da loja, com link para o painel de white label.
// Só leitura — a edição de cada WL vive no painel próprio, e duplicar aqui
// criaria duas fontes de verdade para a mesma configuração.
import { ExternalLinkIcon } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { Badge } from "@/components/ui/badge"
import { FRONTEND_ROUTES } from "@/constants"
import { cn } from "@/lib/utils"
import type { AdminMerchant } from "@/types/admin-merchant"

const panelPrefix = "pages.adminMerchants"

type MerchantWhiteLabelsTabProps = {
  merchant: AdminMerchant
}

export const MerchantWhiteLabelsTab = ({
  merchant,
}: MerchantWhiteLabelsTabProps) => {
  const { t } = useTranslation()
  const whiteLabels = merchant.whiteLabels ?? []

  if (whiteLabels.length === 0) {
    return (
      <div
        data-testid="merchant-white-labels-empty"
        className="flex flex-col items-center gap-2 rounded-xl border border-dashed px-6 py-14 text-center"
      >
        <p className="text-sm text-muted-foreground">
          {t(`${panelPrefix}.whiteLabels.empty`)}
        </p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-4 @2xl:grid-cols-2 @5xl:grid-cols-3">
      {whiteLabels.map((whiteLabel) => {
        const active = Boolean(whiteLabel.active)
        return (
          <Link
            key={whiteLabel.uuid}
            data-testid={`merchant-white-label-${whiteLabel.uuid}`}
            to={FRONTEND_ROUTES.ADMIN_WHITE_LABEL_DETAIL.href.replace(
              ":uuid",
              whiteLabel.uuid
            )}
            className={cn(
              "flex flex-col gap-2 rounded-xl border border-l-4 bg-card p-4 shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              active
                ? "border-l-emerald-500"
                : "border-l-zinc-300"
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-sm font-semibold">
                  {whiteLabel.name ??
                    t(`${panelPrefix}.unnamed`)}
                </span>
                <span className="truncate font-mono text-xs text-muted-foreground">
                  {whiteLabel.slug ?? whiteLabel.uuid}
                </span>
              </div>
              <ExternalLinkIcon
                className="size-4 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className={cn(
                  "rounded-full border font-medium",
                  active
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-zinc-200 bg-zinc-50 text-zinc-500"
                )}
              >
                {t(
                  `${panelPrefix}.${active ? "badgeActive" : "badgeInactive"}`
                )}
              </Badge>
              {whiteLabel.principal && (
                <Badge
                  variant="outline"
                  className="rounded-full border border-sky-200 bg-sky-50 font-medium text-sky-700"
                >
                  {t(
                    `${panelPrefix}.whiteLabels.principal`
                  )}
                </Badge>
              )}
            </div>
          </Link>
        )
      })}
    </div>
  )
}
